import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { US_STATE_CODES, portInUpdateSchema } from '@app/shared';
import type { CustomerDto, PortInDetailDto, PortInUpdateInput } from '@app/shared';
import { FormDialog } from '../../components/dialogs';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { useNotify } from '../../components/NotifyProvider';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { Field, FieldGrid, Section } from './parts';

/**
 * The port-in of this customer that is not finished, on Modify, with the reference's "Query
 * portin", "Update portin" and "Cancel portin". The same request is worked in Ports > Port In.
 */
export function ModifyPortSection({ customer }: { customer: CustomerDto }) {
  const { can } = useAuth();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const allowed = ['PC343', 'PC394', 'PC395', 'PC396'].some((c) => can(c));
  const key = ['customers', customer.id, 'open-port'];
  const port = useQuery({
    queryKey: key,
    queryFn: () =>
      api
        .get<{ data: PortInDetailDto | null }>(`/ports/in/by-customer/${customer.id}`)
        .then((r) => r.data),
    enabled: allowed,
  });
  const act = useMutation({
    mutationFn: (action: 'check-status' | 'cancel') =>
      api.post<PortInDetailDto>(`/ports/in/${port.data!.id}/actions/${action}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  });
  const [editing, setEditing] = useState(false);
  if (!allowed || !port.data) return null;
  const p = port.data;
  const run = async (action: 'check-status' | 'cancel', done: string) => {
    try {
      const next = await act.mutateAsync(action);
      notify.success(`${done} Status: ${next.status}.`);
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };
  return (
    <Section title="Port-in">
      <FieldGrid>
        <Field label="Number to port" value={p.mdn} />
        <Field label="SIM" value={p.newSim} />
        <Field label="Status" value={p.status} />
        <Field label="Carrier response" value={p.carrierResponse} />
      </FieldGrid>
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
        {p.actions.includes('check-status') ? (
          <Button
            variant="outlined"
            disabled={act.isPending}
            onClick={() => void run('check-status', 'Asked the carrier.')}
          >
            Query portin
          </Button>
        ) : null}
        <Button variant="outlined" disabled={act.isPending} onClick={() => setEditing(true)}>
          Update portin
        </Button>
        {p.actions.includes('cancel') ? (
          <Button
            variant="outlined"
            color="error"
            disabled={act.isPending}
            onClick={() => void run('cancel', 'Port-in cancelled.')}
          >
            Cancel portin
          </Button>
        ) : null}
      </Box>
      {editing ? <UpdatePortin port={p} onClose={() => setEditing(false)} /> : null}
    </Section>
  );
}

function UpdatePortin({ port, onClose }: { port: PortInDetailDto; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (input: PortInUpdateInput) =>
      api.post<PortInDetailDto>(`/ports/in/${port.id}/update`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  });
  const { control, handleSubmit, setError } = useForm<PortInUpdateInput>({
    resolver: zodResolver(portInUpdateSchema),
    defaultValues: {
      accountNumber: port.accountNumber,
      passwordPin: '',
      billFirstName: port.billingDetails.firstName,
      billLastName: port.billingDetails.lastName,
      streetNumber: port.billingDetails.streetNumber,
      address1: port.billingDetails.address1,
      streetDirection: port.billingDetails.streetDirection,
      address2: port.billingDetails.address2,
      city: port.billingDetails.city,
      state: port.billingDetails.state as PortInUpdateInput['state'],
      zip: port.billingDetails.zip,
    },
  });
  const submit = handleSubmit(async (values) => {
    try {
      const next = await save.mutateAsync(values);
      notify.success(`Port-in updated. Status: ${next.status}.`);
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  const text = (name: keyof PortInUpdateInput, label: string, max: number, required = false) => (
    <TextInput
      control={control}
      name={name}
      label={label}
      required={required}
      autoComplete="off"
      slotProps={{ htmlInput: { maxLength: max } }}
    />
  );
  return (
    <FormDialog
      open
      title="Update portin"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        {port.manual
          ? 'A manual request: the new details are kept for the person who works it.'
          : 'The request goes to the carrier again with these details.'}
      </Alert>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <Typography sx={{ gridColumn: '1 / -1' }}>SIM: {port.newSim}</Typography>
        {text('accountNumber', 'OSP account number', 40, true)}
        <TextInput
          control={control}
          name="passwordPin"
          label="OSP account password"
          type="password"
          autoComplete="off"
          helperText="Sent to the carrier; not stored"
          slotProps={{ htmlInput: { maxLength: 20 } }}
        />
        <Typography sx={{ gridColumn: '1 / -1', fontWeight: 600 }}>Billing details</Typography>
        {text('billFirstName', 'First name', 40, true)}
        {text('billLastName', 'Last name', 40, true)}
        {text('streetNumber', 'Street number', 20)}
        {text('address1', 'Street name', 120, true)}
        {text('streetDirection', 'Street direction', 30)}
        {text('address2', 'Address line2', 120)}
        {text('city', 'City', 60, true)}
        <SelectInput
          control={control}
          name="state"
          label="State code"
          required
          options={toOptions(US_STATE_CODES)}
        />
        {text('zip', 'Zip', 5, true)}
      </Box>
    </FormDialog>
  );
}
